// frontend/src/COIReviewDashboard.tsx - Process Owner COI Review Dashboard

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { API_URL } from './apiConfig';

interface COIReviewDashboardProps {
  onBack: () => void;
}

interface COIDeclaration {
  id: number;
  process_id: number;
  evaluator_id: number;
  has_conflict: boolean;
  conflict_details: string | null;
  confidentiality_agreed: boolean;
  management_strategy: string | null;
  status: string;
  declared_at: string;
  reviewed_at: string | null;
  review_comments: string | null;
  evaluator_name: string;
  evaluator_email: string;
  process_name: string;
}

interface ReviewFormData {
  status: string;
  review_comments: string;
  management_strategy: string;
}

const COIReviewDashboard: React.FC<COIReviewDashboardProps> = ({ onBack }) => {
  const [declarations, setDeclarations] = useState<COIDeclaration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reviewingId, setReviewingId] = useState<number | null>(null);
  const [reviewForm, setReviewForm] = useState<ReviewFormData>({
    status: '',
    review_comments: '',
    management_strategy: ''
  });

  useEffect(() => {
    fetchPendingReviews();
  }, []);

  const fetchPendingReviews = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = Cookies.get('access_token');
      const response = await axios.get<COIDeclaration[]>(`${API_URL}/api/coi/pending-reviews`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeclarations(response.data);
    } catch (error: any) {
      console.error('Error fetching COI reviews:', error);
      setError(error.response?.data?.detail || 'Failed to load COI reviews');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReview = async (declarationId: number) => {
    if (!reviewForm.status || !reviewForm.review_comments.trim()) {
      alert('Please select a decision and provide review comments');
      return;
    }

    try {
      const token = Cookies.get('access_token');
      await axios.put(`${API_URL}/api/coi/${declarationId}/review`, reviewForm, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Refresh the list
      await fetchPendingReviews();
      
      // Reset form
      setReviewingId(null);
      setReviewForm({ status: '', review_comments: '', management_strategy: '' });
      
      alert('COI declaration reviewed successfully. The evaluator has been notified.');
    } catch (error: any) {
      console.error('Error reviewing COI:', error);
      alert(error.response?.data?.detail || 'Failed to submit review');
    }
  };

  const startReview = (declaration: COIDeclaration) => {
    setReviewingId(declaration.id);
    setReviewForm({
      status: '',
      review_comments: '',
      management_strategy: declaration.management_strategy || ''
    });
  };

  const cancelReview = () => {
    setReviewingId(null);
    setReviewForm({ status: '', review_comments: '', management_strategy: '' });
  };

  const styles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '2rem'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '2rem',
      fontWeight: '700',
      color: '#1f2937',
      marginBottom: '0.5rem'
    },
    subtitle: {
      color: '#6b7280',
      fontSize: '1.1rem'
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '1.5rem',
      marginBottom: '1.5rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e5e7eb'
    },
    conflictCard: {
      border: '2px solid #f59e0b',
      backgroundColor: '#fffbeb'
    },
    noConflictCard: {
      border: '2px solid #10b981',
      backgroundColor: '#f0fdf4'
    },
    conflictBadge: {
      padding: '0.25rem 0.75rem',
      borderRadius: '9999px',
      fontSize: '0.75rem',
      fontWeight: '600',
      backgroundColor: '#fcd34d',
      color: '#92400e'
    },
    noConflictBadge: {
      padding: '0.25rem 0.75rem',
      borderRadius: '9999px',
      fontSize: '0.75rem',
      fontWeight: '600',
      backgroundColor: '#34d399',
      color: '#065f46'
    },
    statusBadge: {
      padding: '0.25rem 0.75rem',
      borderRadius: '9999px',
      fontSize: '0.75rem',
      fontWeight: '600'
    },
    reviewForm: {
      marginTop: '1.5rem',
      padding: '1.5rem',
      backgroundColor: '#f9fafb',
      borderRadius: '8px',
      border: '1px solid #e5e7eb'
    },
    formGroup: {
      marginBottom: '1rem'
    },
    label: {
      display: 'block',
      fontSize: '0.9rem',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '0.5rem'
    },
    input: {
      width: '100%',
      padding: '0.75rem',
      border: '2px solid #e5e7eb',
      borderRadius: '6px',
      fontSize: '0.95rem'
    },
    textarea: {
      width: '100%',
      minHeight: '80px',
      padding: '0.75rem',
      border: '2px solid #e5e7eb',
      borderRadius: '6px',
      fontSize: '0.95rem',
      resize: 'vertical' as const,
      fontFamily: 'inherit'
    },
    radioGroup: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '0.5rem'
    },
    radioOption: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    buttonGroup: {
      display: 'flex',
      gap: '0.75rem',
      marginTop: '1rem'
    },
    button: {
      padding: '0.75rem 1.5rem',
      borderRadius: '6px',
      fontSize: '0.9rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      border: 'none'
    },
    primaryButton: {
      backgroundColor: '#3b82f6',
      color: 'white'
    },
    successButton: {
      backgroundColor: '#10b981',
      color: 'white'
    },
    warningButton: {
      backgroundColor: '#f59e0b',
      color: 'white'
    },
    dangerButton: {
      backgroundColor: '#ef4444',
      color: 'white'
    },
    secondaryButton: {
      backgroundColor: '#6b7280',
      color: 'white'
    },
    backButton: {
      backgroundColor: '#f3f4f6',
      color: '#374151',
      border: '2px solid #d1d5db'
    }
  };

  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: '1.2rem', color: '#374151' }}>Loading COI reviews...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={{...styles.card, borderColor: '#ef4444', backgroundColor: '#fef2f2'}}>
          <div style={{ color: '#dc2626', fontSize: '1.1rem', fontWeight: '600' }}>⚠️ Error</div>
          <div style={{ color: '#dc2626', marginTop: '0.5rem' }}>{error}</div>
          <button onClick={fetchPendingReviews} style={{...styles.button, ...styles.primaryButton, marginTop: '1rem'}}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>COI Review Dashboard</h1>
        <p style={styles.subtitle}>Review and approve conflict of interest declarations from evaluation panel members</p>
      </div>

      {declarations.length === 0 ? (
        <div style={styles.card}>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
            <h3 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
              No Pending Reviews
            </h3>
            <p style={{ color: '#6b7280' }}>
              All COI declarations have been reviewed. New declarations will appear here when submitted.
            </p>
          </div>
        </div>
      ) : (
        <div>
          <div style={{ marginBottom: '1.5rem', fontSize: '0.9rem', color: '#6b7280' }}>
            {declarations.length} declaration{declarations.length !== 1 ? 's' : ''} pending review
          </div>

          {declarations.map((declaration) => (
            <div 
              key={declaration.id} 
              style={{
                ...styles.card,
                ...(declaration.has_conflict ? styles.conflictCard : styles.noConflictCard)
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <div>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: '600', color: '#1f2937', marginBottom: '0.25rem' }}>
                    {declaration.evaluator_name}
                  </h3>
                  <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                    {declaration.evaluator_email} • Process: {declaration.process_name}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <span style={declaration.has_conflict ? styles.conflictBadge : styles.noConflictBadge}>
                    {declaration.has_conflict ? 'Has Conflict' : 'No Conflict'}
                  </span>
                  <span style={{...styles.statusBadge, backgroundColor: '#e5e7eb', color: '#374151'}}>
                    {declaration.status}
                  </span>
                </div>
              </div>

              <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '1rem' }}>
                Declared: {new Date(declaration.declared_at).toLocaleString()}
              </div>

              {declaration.has_conflict && declaration.conflict_details && (
                <div style={{ marginBottom: '1rem' }}>
                  <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                    Conflict Details:
                  </h4>
                  <p style={{ color: '#6b7280', fontSize: '0.95rem', lineHeight: '1.5' }}>
                    {declaration.conflict_details}
                  </p>
                </div>
              )}

              {declaration.management_strategy && (
                <div style={{ marginBottom: '1rem' }}>
                  <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                    Management Strategy:
                  </h4>
                  <p style={{ color: '#6b7280', fontSize: '0.95rem', lineHeight: '1.5' }}>
                    {declaration.management_strategy}
                  </p>
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Confidentiality:</span>
                <span style={{ fontSize: '0.75rem', fontWeight: '600', color: declaration.confidentiality_agreed ? '#059669' : '#dc2626' }}>
                  {declaration.confidentiality_agreed ? '✓ Agreed' : '✗ Not Agreed'}
                </span>
              </div>

              {reviewingId === declaration.id ? (
                <div style={styles.reviewForm}>
                  <h4 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#374151', marginBottom: '1rem' }}>
                    Review Decision
                  </h4>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Decision *</label>
                    <div style={styles.radioGroup}>
                      <label style={styles.radioOption}>
                        <input
                          type="radio"
                          name="status"
                          value="approved"
                          checked={reviewForm.status === 'approved'}
                          onChange={(e) => setReviewForm(prev => ({ ...prev, status: e.target.value }))}
                        />
                        <span style={{ color: '#059669' }}>✓ Approve - Allow evaluation to proceed</span>
                      </label>
                      <label style={styles.radioOption}>
                        <input
                          type="radio"
                          name="status"
                          value="requires_management"
                          checked={reviewForm.status === 'requires_management'}
                          onChange={(e) => setReviewForm(prev => ({ ...prev, status: e.target.value }))}
                        />
                        <span style={{ color: '#d97706' }}>⚠️ Requires Management - Additional controls needed</span>
                      </label>
                      <label style={styles.radioOption}>
                        <input
                          type="radio"
                          name="status"
                          value="declined"
                          checked={reviewForm.status === 'declined'}
                          onChange={(e) => setReviewForm(prev => ({ ...prev, status: e.target.value }))}
                        />
                        <span style={{ color: '#dc2626' }}>✗ Decline - Remove from process</span>
                      </label>
                    </div>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Review Comments *</label>
                    <textarea
                      value={reviewForm.review_comments}
                      onChange={(e) => setReviewForm(prev => ({ ...prev, review_comments: e.target.value }))}
                      placeholder="Provide detailed comments on your decision, including any conditions or requirements..."
                      style={styles.textarea}
                    />
                  </div>

                  {reviewForm.status === 'requires_management' && (
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Updated Management Strategy</label>
                      <textarea
                        value={reviewForm.management_strategy}
                        onChange={(e) => setReviewForm(prev => ({ ...prev, management_strategy: e.target.value }))}
                        placeholder="Specify additional management measures or conditions for this evaluator..."
                        style={styles.textarea}
                      />
                    </div>
                  )}

                  <div style={styles.buttonGroup}>
                    <button
                      onClick={() => handleReview(declaration.id)}
                      style={{
                        ...styles.button,
                        ...(reviewForm.status === 'approved' ? styles.successButton :
                            reviewForm.status === 'requires_management' ? styles.warningButton :
                            styles.dangerButton)
                      }}
                    >
                      Submit Review
                    </button>
                    <button
                      onClick={cancelReview}
                      style={{...styles.button, ...styles.secondaryButton}}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ marginTop: '1rem' }}>
                  <button
                    onClick={() => startReview(declaration)}
                    style={{...styles.button, ...styles.primaryButton}}
                  >
                    Review Declaration
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: '2rem' }}>
        <button onClick={onBack} style={{...styles.button, ...styles.backButton}}>
          ← Back to Dashboard
        </button>
      </div>
    </div>
  );
};

export default COIReviewDashboard;